import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { WorkflowAction } from '../../../api/types/dasTickets.types';

/**
 * ActionHistoryTimeline Props
 */
export interface ActionHistoryTimelineProps {
  history: WorkflowAction[];
}

/**
 * ActionHistoryTimeline Component
 *
 * Chronological timeline of all actions taken on the ticket
 * Shows who took action, when, what action, and comments
 */
const ActionHistoryTimeline: React.FC<ActionHistoryTimelineProps> = ({ history }) => {
  // Get action icon
  const getActionIcon = (action: string) => {
    switch (action.toLowerCase()) {
      case 'submitted':
      case 'created':
        return <IconSystem.file size={16} color="white" />;
      case 'approved':
      case 'recommended':
        return <IconSystem.check size={16} color="white" />;
      case 'rejected':
        return <IconSystem.x size={16} color="white" />;
      case 'escalated':
        return <IconSystem.arrowRight size={16} color="white" />;
      case 'returned':
        return <IconSystem.arrowLeft size={16} color="white" />;
      case 'commented':
        return <IconSystem.edit size={16} color="white" />;
      default:
        return <IconSystem.moreVertical size={16} color="white" />;
    }
  };

  // Get action color
  const getActionColor = (action: string) => {
    switch (action.toLowerCase()) {
      case 'approved':
        return '#10b981'; // green
      case 'rejected':
        return '#ef4444'; // red
      case 'submitted':
      case 'created':
        return '#3b82f6'; // blue
      case 'forwarded':
        return '#8b5cf6'; // purple
      case 'returned':
      case 'request changes':
        return '#f59e0b'; // orange
      case 'withdrawn':
        return '#64748b'; // gray
      default:
        return '#64748b'; // gray
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return 'Just now';
    } else if (diffMins < 60) {
      return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  };

  // Sort history by date (most recent first)
  const sortedHistory = [...history].sort(
    (a, b) => new Date(b.actionDate).getTime() - new Date(a.actionDate).getTime()
  );

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <h3
        style={{
          fontSize: '1.125rem',
          fontWeight: 600,
          marginBottom: '1rem',
          color: '#1e293b',
          borderBottom: '2px solid #e5e7eb',
          paddingBottom: '0.5rem',
        }}
      >
        Action History
      </h3>

      <div
        style={{
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '0.5rem',
          padding: '1rem',
          maxHeight: '400px',
          overflowY: 'auto',
        }}
      >
        {sortedHistory.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#64748b', fontSize: '0.875rem', padding: '1rem' }}>
            No actions recorded yet
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
            {sortedHistory.map((entry, index) => {
              const isLast = index === sortedHistory.length - 1;
              const actionColor = getActionColor(entry.action);

              return (
                <div key={`${entry.actionDate}-${index}`} style={{ position: 'relative' }}>
                  {/* Connecting Line */}
                  {!isLast && (
                    <div
                      style={{
                        position: 'absolute',
                        left: '15px',
                        top: '32px',
                        width: '2px',
                        height: 'calc(100% + 8px)',
                        background: '#e5e7eb',
                      }}
                    />
                  )}

                  {/* History Item */}
                  <div
                    style={{
                      display: 'flex',
                      gap: '0.75rem',
                      marginBottom: isLast ? 0 : '1.5rem',
                      position: 'relative',
                    }}
                  >
                    {/* Action Icon */}
                    <div
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background: actionColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        border: '2px solid white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      }}
                    >
                      {getActionIcon(entry.action)}
                    </div>

                    {/* Action Details */}
                    <div style={{ flex: 1, paddingTop: '0.125rem' }}>
                      {/* Header */}
                      <div style={{ marginBottom: '0.25rem' }}>
                        <span
                          style={{
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: '#1e293b',
                          }}
                        >
                          {entry.actionBy}
                        </span>
                        <span style={{ fontSize: '0.875rem', color: '#64748b', marginLeft: '0.375rem' }}>
                          {entry.action}
                        </span>
                      </div>

                      {/* Timestamp and Level */}
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>
                        {formatDate(entry.actionDate)}
                        {entry.level !== undefined && entry.level > 0 && (
                          <span style={{ marginLeft: '0.5rem' }}>• Level {entry.level}</span>
                        )}
                      </div>

                      {/* Role */}
                      <div
                        style={{
                          fontSize: '0.75rem',
                          color: '#64748b',
                          marginBottom: '0.25rem',
                        }}
                      >
                        {entry.actionByRole}
                      </div>

                      {/* Comments */}
                      {entry.comments && (
                        <div
                          style={{
                            fontSize: '0.875rem',
                            color: '#475569',
                            background: 'white',
                            padding: '0.5rem',
                            borderRadius: '0.375rem',
                            border: '1px solid #e2e8f0',
                            marginTop: '0.5rem',
                            fontStyle: 'italic',
                          }}
                        >
                          "{entry.comments}"
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActionHistoryTimeline;
